import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { MESSAGE_STATUS } from '../../../utils/messageQueue';

const MessageBubble = ({ 
  message, 
  isCurrentUser, 
  showAvatar, 
  showTimestamp,
  onEdit,
  onDelete 
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message?.content || '');
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setShowMenu(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  // Check if message can be edited or deleted (within 1 minute)
  const canModifyMessage = () => {
    if (!isCurrentUser || !message?.timestamp && !message?.createdAt) return false;
    
    const messageTime = new Date(message?.timestamp || message?.createdAt)?.getTime();
    const currentTime = Date.now();
    const timeDifference = currentTime - messageTime;
    
    // 1-minute modification rule (60000 milliseconds = 1 minute)
    return timeDifference < 60000;
  };

  const getStatusIcon = () => {
    if (!isCurrentUser) return null;

    switch (message?.status) {
      case MESSAGE_STATUS?.PENDING:
        return <Icon name="Clock" size={14} color="white" />;
      case MESSAGE_STATUS?.SENDING:
        return <Icon name="Clock" size={14} color="white" className="animate-pulse" />;
      case MESSAGE_STATUS?.SENT:
        return <Icon name="Check" size={16} color="white" />;
      case MESSAGE_STATUS?.DELIVERED:
        return <Icon name="CheckCheck" size={16} color="white" />;
      case MESSAGE_STATUS?.READ:
        return <Icon name="CheckCheck" size={16} color="#93c5fd" />;
      case MESSAGE_STATUS?.FAILED:
        return <Icon name="AlertCircle" size={14} color="#d32f2f" />;
      default:
        return <Icon name="Check" size={16} color="white" />;
    }
  };

  const handleEditSubmit = () => {
    if (editContent?.trim() && editContent?.trim() !== message?.content) {
      onEdit?.(message?.id, editContent?.trim());
    }
    setIsEditing(false);
    setShowMenu(false);
  };

  const handleEditCancel = () => {
    setEditContent(message?.content || '');
    setIsEditing(false);
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (window.confirm('Bu mesajı silmek istediğinizden emin misiniz?')) {
      onDelete?.(message?.id);
    }
    setShowMenu(false);
  };

  return (
    <div className={`flex gap-2 mb-1 ${isCurrentUser ? 'justify-end' : 'justify-start'} group`}>
      {/* Avatar for received messages */}
      {!isCurrentUser && showAvatar && (
        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 self-end mb-1">
          {message?.sender?.avatar ? (
            <img
              src={message?.sender?.avatar}
              alt={message?.sender?.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
              {message?.sender?.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
        </div>
      )}

      {/* Spacer for alignment when no avatar */}
      {!isCurrentUser && !showAvatar && <div className="w-8"></div>}

      {/* Message Container */}
      <div className={`flex flex-col max-w-[65%] ${isCurrentUser ? 'items-end' : 'items-start'}`}>
        {/* Sender name for group chats */}
        {!isCurrentUser && showAvatar && (
          <span className="text-xs font-medium mb-1 px-2 text-blue-600">
            {message?.sender?.name || 'Unknown'}
          </span>
        )}

        {/* Message Bubble */}
        <div className="relative">
          <div
            className={`rounded-lg px-3 py-2 shadow-sm relative ${
              isCurrentUser ? 'bg-blue-600 text-white' : 'bg-white'
            }`}
            style={{
              borderRadius: isCurrentUser 
                ? '8px 0px 8px 8px' :'0px 8px 8px 8px'
            }}
          >
            {/* Edit Mode */}
            {isEditing ? (
              <div className="space-y-2">
                <Input
                  type="text"
                  value={editContent}
                  onChange={(e) => setEditContent(e?.target?.value)}
                  onKeyDown={(e) => {
                    if (e?.key === 'Enter' && !e?.shiftKey) {
                      e?.preventDefault();
                      handleEditSubmit();
                    } else if (e?.key === 'Escape') {
                      handleEditCancel();
                    }
                  }}
                  className="w-full text-sm"
                  style={{ 
                    backgroundColor: isCurrentUser ? '#3b82f6' : '#f5f5f5',
                    border: 'none',
                    color: isCurrentUser ? 'white' : '#111b21'
                  }}
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={handleEditCancel}
                    className="text-xs px-2 py-1 rounded hover:bg-gray-100"
                    style={{ color: isCurrentUser ? 'white' : '#54656f' }}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleEditSubmit}
                    className="text-xs px-2 py-1 rounded bg-blue-700 text-white hover:bg-blue-800"
                  >
                    Kaydet
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Message Content */}
                <p 
                  className="text-[14.2px] leading-[19px] break-words whitespace-pre-wrap" 
                  style={{ color: isCurrentUser ? 'white' : '#111b21' }}
                >
                  {message?.content}
                </p>

                {/* Edited Indicator */}
                {message?.isEdited && (
                  <span 
                    className="text-[11px] italic mr-2" 
                    style={{ color: isCurrentUser ? 'rgba(255,255,255,0.7)' : '#667781' }}
                  >
                    düzenlendi
                  </span>
                )}

                {/* Timestamp and Status */}
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span 
                    className="text-[11px]" 
                    style={{ color: isCurrentUser ? 'rgba(255,255,255,0.8)' : '#667781' }}
                  >
                    {formatTime(message?.timestamp || message?.createdAt)}
                  </span>
                  {isCurrentUser && getStatusIcon()}
                </div>
              </>
            )}

            {/* Tail */}
            <div
              className={`absolute -bottom-0.5 w-2 h-2 ${
                isCurrentUser ? 'bg-blue-600' : 'bg-white'
              }`}
              style={{
                [isCurrentUser ? 'right' : 'left']: '-8px',
                clipPath: isCurrentUser 
                  ? 'polygon(0 0, 100% 0, 100% 100%)'
                  : 'polygon(0 0, 100% 0, 0 100%)'
              }}
            ></div>
          </div>

          {/* Message Actions Menu (only for current user's messages) */}
          {isCurrentUser && !isEditing && canModifyMessage() && (
            <div className="absolute top-0 right-0 -mr-10 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
              >
                <Icon name="ChevronDown" size={16} color="#54656f" />
              </button>

              {/* Dropdown Menu */}
              {showMenu && (
                <div 
                  ref={menuRef}
                  className="absolute right-0 mt-1 py-1 rounded-lg shadow-lg z-10 min-w-[120px] bg-white border border-gray-100"
                >
                  {/* Show Edit option if within 1-minute window */}
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
                    style={{ color: '#111b21' }}
                  >
                    <Icon name="Edit2" size={14} color="#54656f" />
                    Düzenle
                  </button>
                  {/* Show Delete option if within 1-minute window */}
                  <button
                    onClick={handleDelete}
                    className="w-full px-4 py-2 text-sm text-left hover:bg-red-50 flex items-center gap-2"
                    style={{ color: '#d32f2f' }}
                  >
                    <Icon name="Trash2" size={14} color="#d32f2f" />
                    Sil
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Retry Button for Failed Messages */}
        {message?.status === MESSAGE_STATUS?.FAILED && isCurrentUser && (
          <button
            onClick={() => message?.onRetry?.()}
            className="text-xs mt-1 px-2 py-1 rounded hover:bg-red-50 transition-colors"
            style={{ color: '#d32f2f' }}
          >
            <Icon name="RefreshCw" size={12} className="inline mr-1" />
            Tekrar dene
          </button>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;